import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api, { userAPI } from '../services/api';

interface GameState {
  stars: number;
  energy: number;
  spins: number;
  diamonds: number;
  gender: 'male' | 'female' | null;
  isLoading: boolean;
  error: string | null;
  pendingClicks: number;
  lastSyncTime: number;
  syncInterval: number;
  isNewUser: boolean;
  setStars: (stars: number) => void;
  addStars: (amount: number) => void;
  setEnergy: (energy: number) => void;
  addEnergy: (amount: number) => void;
  setSpins: (spins: number) => void;
  addSpins: (amount: number) => void;
  setDiamonds: (diamonds: number) => void;
  addDiamonds: (amount: number) => void;
  updateDiamonds: (newAmount: number) => void;
  useSpins: (amount: number) => boolean;
  useEnergy: (amount: number) => boolean;
  useDiamonds: (amount: number) => boolean;
  hasEnergy: () => boolean;
  hasDiamonds: (amount: number) => boolean;
  fetchUserData: () => Promise<void>;
  processClick: (clickCount?: number) => Promise<any>;
  addPendingClick: (clickCount?: number) => void;
  syncPendingClicks: () => Promise<any>;
  setSyncInterval: (interval: number) => void;
  setGender: (gender: 'male' | 'female') => Promise<boolean>;
  fetchGender: () => Promise<'male' | 'female' | null>;
  hasGender: () => boolean;
}

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      stars: 0,
      energy: 0,
      spins: 0,
      diamonds: 0,
      gender: null,
      isLoading: false,
      error: null,
      pendingClicks: 0,
      lastSyncTime: 0,
      syncInterval: 5000, // По умолчанию синхронизация каждые 5 секунд
      isNewUser: false,

      // Получение данных пользователя с сервера
      fetchUserData: async () => {
        try {
          set({ isLoading: true, error: null });
          const userData = await userAPI.getProfile();

          if (userData) {
            let isNewUser = false;
            try {
              const registeredAtRaw: any = (userData as any).registeredAt || (userData as any).createdAt;
              if (registeredAtRaw) {
                const registeredAt = new Date(registeredAtRaw).getTime();
                if (!Number.isNaN(registeredAt)) {
                  const diffMs = Date.now() - registeredAt;
                  const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;
                  isNewUser = diffMs <= TWO_DAYS_MS;
                }
              }
            } catch (e) {
              console.error('Failed to compute isNewUser:', e);
            }
            set({ 
              stars: parseFloat(userData.stars) || 0,
              energy: userData.energy || 0,
              diamonds: userData.diamonds || 0,
              spins: userData.spins || 0,
              gender: userData.gender || null,
              isNewUser,
              isLoading: false
            });
          }

        } catch (error) {
          console.error('Failed to fetch user data:', error);
          set({ error: 'Не удалось загрузить данные пользователя', isLoading: false });
        }
      },

      // Получение пола пользователя
      fetchGender: async () => {
        try {
          const response = await userAPI.getUserGender();
          if (response && response.gender) {
            set({ gender: response.gender });
            return response.gender;
          }
          return null;
        } catch (error) {
          console.error('Failed to fetch gender:', error);
          return null;
        }
      },
      
      // Установка пола пользователя
      setGender: async (gender: 'male' | 'female') => {
        try {
          set({ isLoading: true });
          const response = await userAPI.setUserGender(gender);
          if (response && response.success) {
            set({ gender, isLoading: false });
            return true;
          }
          set({ isLoading: false });
          return false;
        } catch (error) {
          console.error('Failed to set gender:', error);
          set({ isLoading: false });
          return false;
        }
      },
      
      // Проверка, указан ли пол пользователя
      hasGender: () => {
        return get().gender !== null;
      },
      
      // Добавление клика в очередь ожидания
      addPendingClick: (clickCount = 1) => {
        const currentEnergy = get().energy;
        const pendingClicks = get().pendingClicks;
        
        // Проверяем, достаточно ли энергии с учетом уже накопленных кликов
        if (currentEnergy < 1) {
          return false;
        }
        
        // Обновляем локальное состояние
        set(state => ({
          pendingClicks: state.pendingClicks + clickCount,
          energy: state.energy - clickCount,
          stars: parseFloat((state.stars + (clickCount * 0.1)).toFixed(1))
        }));
        
        // Проверяем, нужно ли синхронизироваться с сервером
        const now = Date.now();
        const lastSync = get().lastSyncTime;
        const interval = get().syncInterval;
        
        if (now - lastSync >= interval) {
          get().syncPendingClicks();
        }
        
        return true;
      },
      
      // Синхронизация накопленных кликов с сервером
      syncPendingClicks: async () => {
        const pendingClicks = get().pendingClicks;
        
        if (pendingClicks <= 0) {
          return null;
        }
        
        try {
          set({ isLoading: true });
          const response = await userAPI.addClick(pendingClicks);
          
          if (response) {
            const processedClicks = response.processedClicks || 0;
            
            // Если обработаны все клики
            if (processedClicks >= pendingClicks) {
              set({ 
                pendingClicks: 0,
                stars: parseFloat(response.newBalance) || get().stars,
                energy: response.energyLeft || get().energy,
                lastSyncTime: Date.now(),
                isLoading: false
              });
            } 
            // Если обработаны не все клики (например, закончилась энергия)
            else {
              // Вычисляем, сколько кликов осталось необработанными
              const remainingClicks = pendingClicks - processedClicks;
              
              
              // Сбрасываем все накопленные клики, так как их нельзя обработать из-за нехватки энергии
              set({ 
                pendingClicks: 0,
                stars: parseFloat(response.newBalance) || get().stars,
                energy: response.energyLeft || get().energy,
                lastSyncTime: Date.now(),
                isLoading: false
              });
            }
            
            return response;
          }
        } catch (error: any) {
          console.error('Failed to sync clicks:', error);
          
          // Для всех ошибок просто устанавливаем флаг ошибки
          set({ error: 'Не удалось синхронизировать клики', isLoading: false });
          
          return null;
        }
      },
      
      // Установка интервала синхронизации
      setSyncInterval: (interval: number) => {
        set({ syncInterval: interval });
      },
      
      // Обработка клика с отправкой на сервер (для обратной совместимости)
      processClick: async (clickCount = 1) => {
        return get().addPendingClick(clickCount);
      },
      
      setStars: (stars: number) => set({ stars }),
      addStars: (amount: number) => set((state) => ({ stars: +(state.stars + amount).toFixed(1) })),
      setEnergy: (energy: number) => set({ energy }),
      addEnergy: (amount: number) => set((state) => ({ energy: state.energy + amount })),
      setSpins: (spins: number) => set({ spins }),
      addSpins: (amount: number) => set((state) => ({ spins: state.spins + amount })),
      setDiamonds: (diamonds: number) => set({ diamonds }),
      addDiamonds: (amount: number) => set((state) => ({ diamonds: state.diamonds + amount })),
      updateDiamonds: (newAmount: number) => set({ diamonds: newAmount }),
      useSpins: (amount: number) => {
        const currentSpins = get().spins;
        if (currentSpins >= amount) {
          set((state) => ({ spins: state.spins - amount }));
          return true;
        }
        return false;
      },
      useEnergy: (amount: number) => {
        const currentEnergy = get().energy;
        if (currentEnergy >= amount) {
          set((state) => ({ energy: state.energy - amount }));
          return true;
        }
        return false;
      },
      useDiamonds: (amount: number) => {
        const currentDiamonds = get().diamonds;
        if (currentDiamonds >= amount) {
          set((state) => ({ diamonds: state.diamonds - amount }));
          return true;
        }
        return false;
      },
      hasEnergy: () => {
        return get().energy > 0;
      },
      hasDiamonds: (amount: number) => {
        return get().diamonds >= amount;
      }
    }),
    {
      name: 'starsClickerState',
    }
  )
); 